import React, { FunctionComponent } from 'react';
import * as ioTs from 'io-ts';
import { PartOfSpeech, getPartOfSpeechByCode } from 'app/constants/enums/PartOfSpeech';
import { tFunctionType } from 'app/services/localization';
import Badge from 'app/components/uknow/ui/Badge';
import { GREY_S, PURE_WHITE } from 'app/constants/Colors';
import { useI18n } from 'app/components/localization';
import AlertBox, { AlertVariant } from 'app/components/presentation/alert/AlertBox';
import { useClasses } from 'app/utils/css';
import FlexMarginGroup from 'app/components/layout/FlexMarginGroup';
import Tag from 'app/components/presentation/containers/Tag';

export enum RelationType {
  Related = 'related',
  Synonym = 'synonym',
  Antonym = 'antonym',
}

const RelationNameMap: {[k in RelationType]: (t: tFunctionType) => string } = {
  [RelationType.Related]: (t) => t('uknow.answer.word.relation-type.related'),
  [RelationType.Synonym]: (t) => t('uknow.answer.word.relation-type.synonym'),
  [RelationType.Antonym]: (t) => t('uknow.answer.word.relation-type.antonym'),
};

const PartOfSpeechFromCode = new ioTs.Type<PartOfSpeech, string, string>(
  'PartOfSpeechFromCode',
  (x: unknown): x is PartOfSpeech => x instanceof PartOfSpeech,
  (s, c) => {
    const pos = getPartOfSpeechByCode(s);
    return pos === undefined ? ioTs.failure(s, c) : ioTs.success(pos);
  },
  (pos) => pos.metadata.code,
);

export const WordAttributes = ioTs.strict({
  pos: ioTs.string.pipe(PartOfSpeechFromCode),
  text: ioTs.string,
  translation: ioTs.string,
  relation: ioTs.union([
    ioTs.undefined,
    ioTs.keyof({
      [RelationType.Related]: null,
      [RelationType.Antonym]: null,
      [RelationType.Synonym]: null,
    }),
  ]),
  note: ioTs.union([ioTs.undefined, ioTs.string]),
});

type Props = ioTs.TypeOf<typeof WordAttributes>;

const Word: FunctionComponent<Props> = ({
  pos,
  text,
  translation,
  relation = RelationType.Related,
  note,
}) => {
  const { t } = useI18n();
  const classes = useClasses({
    wordContainer: {
      border: `2px solid ${GREY_S}`,
      margin: '15px 0',
    },
    content: {
      padding: '15px',
    },
    badgeText: {
      color: PURE_WHITE,
    },
    strong: {
      fontWeight: 600,
    },
  });
  return (
    <div className={classes.wordContainer} data-testid="uknow-word">
      <Badge color={GREY_S}>
        <span className={classes.badgeText}>
          {RelationNameMap[relation](t)}
        </span>
      </Badge>
      <div className={classes.content}>
        <FlexMarginGroup vertical margin="15px">
          {note &&
            // Since the note is shown as auxiliary information and not time critical do not set an alert role.
            <AlertBox variant={AlertVariant.Info}>{note}</AlertBox>}
          <span className={classes.strong}>{text}</span>
          <FlexMarginGroup margin="10px">
            <Tag>{pos.getName(t)}</Tag>
            <span className={classes.strong}>{translation}</span>
          </FlexMarginGroup>
        </FlexMarginGroup>
      </div>
    </div>
  );
};

export default Word;
